import { BIMElement } from './types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Clipboard Manager
 * 
 * Handles Cut, Copy, Paste operations for BIM elements.
 * Uses internal storage to bypass browser clipboard restrictions for complex objects.
 */
class ClipboardManagerService {
    private buffer: BIMElement[] = [];

    /**
     * Copy elements to buffer
     */
    copy(elements: BIMElement[]) {
        if (!elements || elements.length === 0) return;

        // Deep clone to detach from store
        this.buffer = structuredClone(elements);
        console.log(`Clipboard: Copied ${elements.length} elements.`);
    }

    /**
     * Cut elements (Copy + Delete callback)
     * The actual deletion should be handled by the caller/tool via BIMStore.
     */
    cut(elements: BIMElement[]) {
        this.copy(elements);
        console.log(`Clipboard: Cut ${elements.length} elements.`);
    }

    /**
     * Paste elements from buffer
     * Returns new elements with generated IDs, ready to be added to store.
     * 
     * @param offset Optional offset vector to apply to position
     */
    paste(offset: { x: number, y: number, z: number } = { x: 1, y: 1, z: 0 }): BIMElement[] {
        if (this.buffer.length === 0) {
            console.warn('Clipboard: Buffer empty.');
            return [];
        }

        return this.buffer.map(el => {
            const newEl = structuredClone(el);
            newEl.id = uuidv4(); // Generate new ID

            // Offset position if applicable
            if (newEl.geometryData && newEl.geometryData.position) {
                newEl.geometryData.position[0] += offset.x; // Shift X
                newEl.geometryData.position[1] += offset.y; // Shift Y
                newEl.geometryData.position[2] += offset.z; // Shift Z (usually 0)

                // For walls/lines with start/end
                if (newEl.geometryData.start) {
                    newEl.geometryData.start[0] += offset.x;
                    newEl.geometryData.start[1] += offset.y;
                    newEl.geometryData.start[2] += offset.z;
                }
                if (newEl.geometryData.end) {
                    newEl.geometryData.end[0] += offset.x;
                    newEl.geometryData.end[1] += offset.y;
                    newEl.geometryData.end[2] += offset.z;
                }
            } else if (newEl.geometryData && newEl.geometryData.start && newEl.geometryData.end) {
                // Walls without 'position' property at top level?
                newEl.geometryData.start[0] += offset.x;
                newEl.geometryData.start[1] += offset.y;
                newEl.geometryData.start[2] += offset.z;

                newEl.geometryData.end[0] += offset.x;
                newEl.geometryData.end[1] += offset.y;
                newEl.geometryData.end[2] += offset.z;
            }

            return newEl;
        });
    }

    get hasContent(): boolean {
        return this.buffer.length > 0;
    }
}

export const ClipboardManager = new ClipboardManagerService();
